import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import PlugNav from '../containers/_PlugNav';
import MainLayout from '../layouts/MainLayout';
import {
  AISecurity,
  AttackPathAnalysis,
  // Findings,
  BucketProtections,
  ChangePassword,
  ApplicationSecurity,
  AttackSurface,
  Compliance,
  ComplianceBreakdown,
  CompliancePack,
  ConnectCloudAccount,
  DashboardNew,
  DastEnvironmentDetails,
  Discovery,
  EOL,
  Error,
  FinOps,
  EventsTable,
  Exceptions,
  IdentityNew,
  Inventory,
  K8Malware,
  KubernetesCompliance,
  KubernetesEnforcement,
  KubernetesIdentity,
  KubernetesInventory,
  KubernetesInventoryTable,
  KubernetesNetwork,
  KubernetesOverview,
  KubernetesRiskAcceptance,
  KubernetesRuntime,
  KubernetesSeccomp,
  KubernetesImageScout,
  MalwareOverview,
  MalwareOverviewNew,
  MalwareOverviewStatic,
  NewScan,
  NewTemplateEditor,
  Notifications,
  Profile,
  RBACIndex,
  Remediation,
  RemediationHub,
  RepositoryIntegration,
  Risk,
  RiskTable,
  RulesNew,
  RulesTable,
  ScanHistory,
  ScanReport2,
  Threat,
  ViperAI,
  AIChat,
  Workload,
  APISecurityOverview,
  WorkSpace,
  // MalwareOverviewNew,
} from './loadable';

// Import ZendeskLogin component
import ZendeskLogin from '../pages/zendesk-login';

import AddRemediationPolicy from '@root/containers/remediation-policy/newPolicy';

import RouteLoader from '@components/v2/global/RouteLoader';

import AppLogoLoader from '@root/components/AppLogoLoader';
import ErrorBoundaryMessage from '@root/components/v2/global/ErrorBoundaryMessage/index.jsx';
import CompliacneIndividualSkleton from '@root/containers/compliance/individual/skeleton';
import CompliacneSkleton from '@root/containers/compliance/skeleton';
import EnvironmentSkleton from '@root/containers/environment/skeleton';
import RbacSkleton from '@root/containers/rbac/skeleton';
import PolicySkeleton from '@root/containers/remediation-policy/PolicySkeleton';
import ReportSkeleton from '@root/containers/remediation-report/ReportSkeleton';
import RiskSkeleton from '@root/containers/risk/skeleton';
import RiskTableSkeleton from '@root/containers/risk/table/skeleton';
import ExceptionTableSkleton from '@root/containers/rules/exceptions/skeleton';
import { useSelectedEnvironments } from '@root/hooks/environments';
import EventOverviewSkleton from '@root/pages/events/skeleton';
import EventTableSkleton from '@root/pages/events/Table/skeleton';
import ScanTableSkleton from '@root/pages/history/skeleton';
import OverviewSkeleton from '@root/pages/identity/pages/overview/skeleton';
import IdentityTableSkeleton from '@root/pages/identity/pages/overview/table/skeleton';
import UsersSkeleton from '@root/pages/identity/pages/users/skeleton';
import RolesSkeleton from '@root/pages/identity/pages/roles/skeleton';
import PoliciesSkeleton from '@root/pages/identity/pages/policies/skeleton';
import GroupsSkeleton from '@root/pages/identity/pages/groups/skeleton';
import IdPSkeleton from '@root/pages/identity/pages/idp/skeleton';
import NonHumanIdentitySkeleton from '@root/pages/identity/pages/nonHumanIdentity/skeleton';
import NotificationSkleton from '@root/pages/notifications/skeleton';
import RulesTableSkleton from '@root/pages/rules_new/rulesTable/skeleton';
import RulesSkeleton from '@root/pages/rules_new/skeleton';
import WorkloadSkeleton from '@root/pages/workloload-scanning/WorkloadSkeleton';
// import MalwareSkeleton from '@root/pages/Malware/Overview/skeleton';
// import FindingTableSkleton from '@root/pages/Malware/Findings/skeleton';
import DashboardSkeleton from '@root/containers/dashboard/skeleton';
import { INVENTORY_BASE_PATH, INVENTORY_TAB_PARAM, resolveInventoryTab } from '@root/constants/inventoryTabs';
import { FINOPS_BASE_PATH, FINOPS_TAB_PARAM } from '@root/constants/finOpsTabs';
import { WORKSPACE_BASE_PATH, WORKSPACE_TAB_PARAM, resolveWorkspaceTab } from '@root/constants/workspaceTabs';
import {
  AI_SECURITY_TABS,
  API_SECURITY_TABS,
  APPLICATION_SECURITY_TABS,
  DATABASE_SECURITY_TABS,
  IDENTITY_TABS,
  LEGACY_CDR_TABS,
  LEGACY_CDR_TAB_PARAM,
  REMEDIATION_HUB_TABS,
  THREAT_TABS,
  WORKLOAD_TABS,
  resolveThreatEventsTab,
  resolveThreatTab,
} from '@root/constants/moduleTabs';
import ResourceTableSkleton from '@root/pages/inventory-overview/resource-evaluation/skeleton';

import KeyInsightSkeleton from '@root/pages/inventory-overviewNew/key-insights/skeleton';
import ExplorerSkeleton from '@root/pages/visualization/skeleton';
import InventoryOverviewSkeleton from '@root/pages/inventory1.3/skeleton';
import K8Vulnerabilities from '@root/pages/kubernetes/pages/vulnerabilities';
import BucketTableSkleton from '@root/pages/Malware/BucketProtection/skeleton';
import DataSecuritySkeleton from '@root/pages/Malware/Overview/skeleton-new';
import CodeRepoSkeleton from '@root/pages/code/repository/skeleton';
import { IacOnboarding } from '@root/pages/notifications/pages/Channels/Onboarding/IacOnboarding';
import SecurityImprovementDashboard from '@root/pages/SecurityScoreDashboard';
import ViperAIChat from '@root/pages/ViperAI/Dashboard';
import { sendErrorToGoogleChat } from '@root/utils/errorNotification';

// const webhookUrl = import.meta.env.VITE_GOOGLE_CHAT_WEBHOOK_URL;
const isProd = import.meta.env.VITE_AGENT_API_URL_ROOT;

import K8ActionHistory from '@root/pages/kubernetes/pages/action-history';
import ProgressTracker from '@root/pages/ProgressTracker/progressTracker';
import KubernetesScanHistory from '@root/pages/kubernetes/pages/scan-history';
import DatabaseSecurityModule from '@root/pages/database-security/module';

// One wrapper per route component, built once. Building it on every render gave React a new component
// type each time AppRoutes re-rendered — which it does on every location change — so changing only the
// query string (a page's `?currentTab=` tab, say) unmounted and remounted the whole page, refetching
// everything and dropping its scroll position.
const errorRouteComponentCache = new WeakMap();
const errorRouteComponentHoc = Component => {
  if (!Component) return Component;
  let WrappedComponent = errorRouteComponentCache.get(Component);
  if (!WrappedComponent) {
    WrappedComponent = props => (
      <ErrorBoundary>
        <Component {...props} />
      </ErrorBoundary>
    );
    errorRouteComponentCache.set(Component, WrappedComponent);
  }
  return WrappedComponent;
};
const CustomRoute = ({ path, component, children, ...props }) => {
  return (
    <Route path={path} component={errorRouteComponentHoc(component)} {...props}>
      {children}
    </Route>
  );
};

// Legacy deep links: /inventory/resources-table?<query> -> /inventory?tab=resources&<query>.
// The resources table is the Resources tab of the single /inventory route now; the rest of the query
// string is carried through unchanged so nothing that already links here breaks.
const RedirectToResourcesTab = ({ location }) => {
  const params = new URLSearchParams(location?.search || '');
  params.delete(INVENTORY_TAB_PARAM);
  const rest = params.toString();

  return <Redirect to={`${INVENTORY_BASE_PATH}?${INVENTORY_TAB_PARAM}=resources${rest ? `&${rest}` : ''}`} />;
};

// Preserve the old multi-route module URLs while moving module navigation to one query-tab route.
// Reads location via useLocation() rather than a `location` prop: every call site renders this as
// <Route> children (not `component`), and react-router only injects route props onto `component`.
// `nestedTab` is for modules whose module tab owns a second level of tabs (Threat's Events tab). It
// is only filled in when the incoming link pins none, so deep links such as
// `/events/all?tab=Threat` keep the view they asked for.
const RedirectToModuleTab = ({ basePath, tabParam, tab, nestedTabParam = 'tab', nestedTab }) => {
  const location = useLocation();
  const params = new URLSearchParams(location?.search || '');
  params.set(tabParam, tab);
  if (nestedTab && !params.get(nestedTabParam)) params.set(nestedTabParam, nestedTab);
  return <Redirect to={`${basePath}?${params.toString()}`} />;
};

// Legacy deep links: `/cdr` -> `/threat`. CDR was renamed to Threat, and its Overview and All
// Events module tabs became nested tabs of the single Events tab, so the old `?cdrTab=` value maps
// to a module tab plus (for the two that merged) the nested tab that preserves the old view.
const RedirectCdrToThreat = () => {
  const location = useLocation();
  const params = new URLSearchParams(location?.search || '');
  const legacy = LEGACY_CDR_TABS[params.get(LEGACY_CDR_TAB_PARAM)];

  params.delete(LEGACY_CDR_TAB_PARAM);
  params.set(THREAT_TABS.tabParam, legacy?.tab ?? THREAT_TABS.defaultTab);
  if (legacy?.nestedTab && !params.get('tab')) params.set('tab', legacy.nestedTab);

  return <Redirect to={`${THREAT_TABS.basePath}?${params.toString()}`} />;
};

// Legacy deep links: /explorer and /visualization -> /inventory?tab=explorer. Explorer is a tab of
// the single /inventory route now; any other query params are carried through unchanged.
const RedirectToExplorerTab = ({ location }) => {
  const params = new URLSearchParams(location?.search || '');
  params.delete(INVENTORY_TAB_PARAM);
  const rest = params.toString();

  return <Redirect to={`${INVENTORY_BASE_PATH}?${INVENTORY_TAB_PARAM}=explorer${rest ? `&${rest}` : ''}`} />;
};
// console.log(webhookUrl, 'webhookUrl');
// This error boundary is placed here and not near the "root" of the app
// because we want to be able to show an error UI inside the "content area".
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  componentDidCatch(error, errorInfo) {
    const errorMessage = error.message?.toLowerCase() || '';
    const isModuleLoadError = new RegExp('(failed.*load.*script)|(failed.*fetch.*module)', 'ig').test(errorMessage);

    if (isModuleLoadError) {
      console.error('Caught module load error. Reloading the page.');
      window.location.reload();
    } else {
      this.setState({
        error: error,
        errorInfo: errorInfo,
      });

      // Send error notification to Google Chat in production
      if (isProd?.includes('https://api.cyberqshield.seclogic.ai')) {
        sendErrorToGoogleChat(error);
      }
    }
  }

  render() {
    if (this.state.errorInfo) {
      // Error path
      return <ErrorBoundaryMessage />;
    }
    // Normally, just render children
    return this.props.children;
  }
}

function AppRoutes({ ...props }) {
  const { pathname, search } = useLocation();
  // Four Scenarios

  // Fresh login without AWS account.
  // Fresh login with AWS account added but no scans
  // Fresh login with AWS account added
  // const email = localStorage.getItem('User');
  // console.log(JSON.parse(email)?.user?.email, 'email');
  // const email = localStorage.getItem('User');
  // console.log(JSON.parse(email)?.user?.email, 'email');

  const [canShowPage, setCanShowPage] = useState(false);

  const { selectedEnvironments } = useSelectedEnvironments();
  // const { onboarding } = useEnvironmentsData();

  useEffect(() => {
    if (window.localStorage.getItem('dashboardFromLogin')) {
      window.localStorage.removeItem('dashboardFromLogin');
    } else {
      setCanShowPage(true);
    }
  }, [canShowPage]);
  // console.log(canShowPage, 'canShowPage');
  const fallback = useMemo(() => {
    switch (pathname) {
      // The recently consolidated modules use a single route and choose their view with `?tab=`.
      // Keep their initial loading state as specific as the Inventory module's.
      case '/identity': {
        const tab = IDENTITY_TABS.resolve(search);
        const humanIdentityTab = new URLSearchParams(search).get('humanIdentityTab');
        if (tab === 'human-identity' && humanIdentityTab === 'roles') return <RolesSkeleton />;
        if (tab === 'human-identity' && humanIdentityTab === 'policies') return <PoliciesSkeleton />;
        if (tab === 'human-identity' && humanIdentityTab === 'groups') return <GroupsSkeleton />;
        if (tab === 'human-identity' && (humanIdentityTab === 'idp' || humanIdentityTab === 'jit'))
          return <IdPSkeleton />;
        if (tab === 'human-identity') return <UsersSkeleton />;
        if (tab === 'non-human-identity') return <NonHumanIdentitySkeleton />;
        return <OverviewSkeleton />;
      }
      case '/remediation-hub': {
        return REMEDIATION_HUB_TABS.resolve(search) === 'reports' ? <ReportSkeleton /> : <PolicySkeleton />;
      }
      case '/threat': {
        return resolveThreatTab(search) === 'events' && resolveThreatEventsTab(search) === 'Overview' ? (
          <EventOverviewSkleton />
        ) : (
          <EventTableSkleton />
        );
      }
      case '/workload': {
        return WORKLOAD_TABS.resolve(search) === 'vulnerabilities' ? <WorkloadSkeleton /> : <RouteLoader />;
      }
      case '/application-security': {
        return APPLICATION_SECURITY_TABS.resolve(search) === 'repository-scanning' ? (
          <CodeRepoSkeleton />
        ) : (
          <RouteLoader />
        );
      }
      case '/workspace': {
        const tab = resolveWorkspaceTab(search);
        if (tab === 'environments') return <EnvironmentSkleton />;
        if (tab === 'rbac') return <RbacSkleton />;
        return <RouteLoader />;
      }
      case '/finops': {
        return <RouteLoader />;
      }
      case '/identity/overview': {
        return <OverviewSkeleton />;
      }
      case '/identity/overview/table': {
        return <IdentityTableSkeleton />;
      }
      case '/vulnerabilities': {
        return <WorkloadSkeleton />;
      }
      case '/remediation-policy': {
        return <PolicySkeleton />;
      }
      case '/remediation-report': {
        return <ReportSkeleton />;
      }
      case '/events/all': {
        return <EventTableSkleton />;
      }
      case '/events/suppressed': {
        return <EventTableSkleton />;
      }
      case '/events/overview': {
        return <EventOverviewSkleton />;
      }
      case '/risk/risk-table': {
        return <RiskTableSkeleton />;
      }
      case '/risk': {
        return <RiskSkeleton />;
      }
      case '/data-security/overview': {
        return <DataSecuritySkeleton />;
      }
      case '/data-security': {
        return <DataSecuritySkeleton />;
      }
      // case '/data-security/findings': {
      //   return <FindingTableSkleton />;
      // }
      case '/data-security/insights': {
        return <BucketTableSkleton />;
      }
      case '/code/repository-scanning': {
        return <CodeRepoSkeleton />;
      }
      // The Inventory module is one route; its skeleton follows the `?tab=` selection.
      case '/inventory': {
        const tab = resolveInventoryTab(search);
        if (tab === 'resources') return <ResourceTableSkleton />;
        if (tab === 'key-insight') return <KeyInsightSkeleton />;
        if (tab === 'explorer') return <ExplorerSkeleton />;
        return <InventoryOverviewSkeleton />;
      }
      case '/dashboard': {
        return <DashboardSkeleton />;
      }
      case '/notifications': {
        return <NotificationSkleton />;
      }
      case '/notifications/logs': {
        return <NotificationSkleton />;
      }
      case '/rbac': {
        return <RbacSkleton />;
      }
      case '/exceptions': {
        return <ExceptionTableSkleton />;
      }
      case '/scan-history': {
        return <ScanTableSkleton />;
      }
      case '/rules': {
        return <RulesSkeleton />;
      }
      case '/rules/table': {
        return <RulesTableSkleton />;
      }
      case '/compliance': {
        return <CompliacneSkleton />;
      }
      case '/compliance/breakdown': {
        return <CompliacneIndividualSkleton />;
      }
      case '/environments': {
        return <EnvironmentSkleton />;
      }
      case '/dashboard/templates/new-template': {
        return <NewTemplateEditor />;
      }
      default:
        return <RouteLoader />;
    }
  }, [pathname, search]);

  return (
    <>
      {!canShowPage ? (
        <AppLogoLoader />
      ) : (
        <MainLayout topNav={<PlugNav user={props.user} />}>
          <Suspense fallback={fallback}>
            {selectedEnvironments.map(item => item.id).length > 0 ? (
              <ErrorBoundary>
                <Switch>
                  <CustomRoute exact path="/">
                    <Redirect to={'/dashboard'} />
                  </CustomRoute>
                  {/* <CustomRoute path="/dashboard" exact component={Dashboard} /> */}

                  <CustomRoute path="/dashboard/risk" exact component={Risk} />
                  <CustomRoute path="/security-score" exact component={SecurityImprovementDashboard} />
                  <CustomRoute path="/environments/dashboard/risk" exact component={Risk} />

                  <CustomRoute path="/dashboard/environments" exact>
                    <RedirectToModuleTab
                      basePath={WORKSPACE_BASE_PATH}
                      tabParam={WORKSPACE_TAB_PARAM}
                      tab="environments"
                    />
                  </CustomRoute>

                  <CustomRoute path="/dashboard/environments/scan-report" exact component={ScanReport2} />
                  <CustomRoute path="/environments/scan-report/events" exact component={EventsTable} />
                  <CustomRoute path="/dashboard/environments/connect" exact component={ConnectCloudAccount} />
                  <CustomRoute path="/dashboard/scan-report" exact component={ScanReport2} />
                  <CustomRoute path="/dashboard/compliance" exact component={Compliance} />
                  <CustomRoute path="/dashboard/compliance/breakdown" exact component={ComplianceBreakdown} />
                  <CustomRoute path="/dashboard/compliance/breakdown/scan-report" exact component={ScanReport2} />
                  <CustomRoute path="/dashboard/new" exact component={NewTemplateEditor} />
                  <CustomRoute path={`/dashboard/:data`} exact component={NewTemplateEditor} />
                  {/* <CustomRoute path="/dashboard/inventory/resource-evaluation" exact component={ResourceEvaluation} /> */}
                  <CustomRoute
                    path="/dashboard/inventory/resource-evaluation/scan-report/:id"
                    exact
                    component={ScanReport2}
                  />
                  {/* endregion */}
                  {/* region: Environments Routes */}
                  <CustomRoute path="/environments" exact>
                    <RedirectToModuleTab
                      basePath={WORKSPACE_BASE_PATH}
                      tabParam={WORKSPACE_TAB_PARAM}
                      tab="environments"
                    />
                  </CustomRoute>
                  <CustomRoute path="/environments/notifications/settings" component={Notifications} />
                  <CustomRoute path="/environments/dashboard/rbac" exact component={RBACIndex} />
                  <CustomRoute path="/environments/scan-report" exact component={ScanReport2} />
                  <CustomRoute path="/environments/connect" exact component={ConnectCloudAccount} />
                  <CustomRoute path="/viper" exact component={ViperAIChat} />
                  {/* endregion */}
                  {/* region: Inventory Routes */}
                  {/* <CustomRoute path="/inventory/resource-evaluation" exact component={ResourceEvaluation} /> */}
                  <CustomRoute path="/inventory/resource-evaluation/scan-report" exact component={ScanReport2} />
                  {/* endregion */}
                  {/* region: Compliance Routes */}
                  <CustomRoute path="/compliance" exact component={Compliance} />
                  <CustomRoute path="/compliance/breakdown" exact component={ComplianceBreakdown} />
                  <CustomRoute path="/compliance/breakdown/scan-report" exact component={ScanReport2} />
                  {/* endregion */}

                  {/* New Identity routes */}
                  <CustomRoute path="/identity" component={IdentityNew} />
                  <CustomRoute path="/vulnerabilities" exact>
                    <RedirectToModuleTab basePath={WORKLOAD_TABS.basePath} tabParam="tab" tab="vulnerabilities" />
                  </CustomRoute>
                  <CustomRoute path="/vulnerability/management" exact>
                    <RedirectToModuleTab basePath={WORKLOAD_TABS.basePath} tabParam="tab" tab="dashboard" />
                  </CustomRoute>
                  <CustomRoute path="/workload" exact component={Workload} />

                  {/* New Cloud Events routes */}
                  <CustomRoute path="/events/all/scan-report" exact component={ScanReport2} />
                  {/* <CustomRoute path="/old-events/" exact component={OldEvents} /> */}
                  <CustomRoute path="/events/all/risk" exact component={Risk} />

                  {/* region: Scan History Routes */}
                  <CustomRoute path="/scan-history" exact component={ScanHistory} />
                  <CustomRoute path="/scan-history/scanner" exact component={NewScan} />
                  <CustomRoute path="/scan-history/scan-report" component={ScanReport2} />
                  {/* endregion */}
                  {/* scan histoy page 2 */}

                  <CustomRoute path="/scan-history/scan-report-2" exact component={ScanReport2} />
                  <CustomRoute path="/code/registry-scanning" exact>
                    <RedirectToModuleTab
                      basePath={APPLICATION_SECURITY_TABS.basePath}
                      tabParam="tab"
                      tab="registry-scanning"
                    />
                  </CustomRoute>
                  <CustomRoute path="/code/repository-scanning" exact>
                    <RedirectToModuleTab
                      basePath={APPLICATION_SECURITY_TABS.basePath}
                      tabParam="tab"
                      tab="repository-scanning"
                    />
                  </CustomRoute>
                  <CustomRoute path="/code/dast-scanning" exact>
                    <RedirectToModuleTab
                      basePath={APPLICATION_SECURITY_TABS.basePath}
                      tabParam="tab"
                      tab="dast-scanning"
                    />
                  </CustomRoute>
                  <CustomRoute path="/application-security" exact component={ApplicationSecurity} />
                  <CustomRoute
                    path="/code/dast-scanning/environment-details"
                    exact
                    component={DastEnvironmentDetails}
                  />
                  <CustomRoute path="/workspace/registry-integration" exact component={RepositoryIntegration} />
                  <CustomRoute path="/workspace/repository-integration" exact component={RepositoryIntegration} />
                  <CustomRoute path="/api/cloudsecurity/iac/oauth_callback" exact component={IacOnboarding} />

                  <CustomRoute path="/remediation-report" exact>
                    <RedirectToModuleTab basePath={REMEDIATION_HUB_TABS.basePath} tabParam="tab" tab="reports" />
                  </CustomRoute>
                  <CustomRoute path="/remediation-policy" exact>
                    <RedirectToModuleTab basePath={REMEDIATION_HUB_TABS.basePath} tabParam="tab" tab="policies" />
                  </CustomRoute>
                  <CustomRoute path="/remediation-policy/edit/:policyId" exact component={AddRemediationPolicy} />
                  <CustomRoute path="/remediation-policy/create" exact component={AddRemediationPolicy} />
                  <CustomRoute path="/remediation-hub" exact component={RemediationHub} />
                  <CustomRoute path="/attack-surface" exact component={AttackSurface} />

                  <CustomRoute path="/profile/change-password" exact component={ChangePassword} />

                  {/* Zendesk SSO Login Route */}
                  <CustomRoute path="/zendesk-login" exact component={ZendeskLogin} />

                  {/* EOL Route */}
                  <CustomRoute path="/eol" exact component={EOL} />

                  {/* AI Security Route */}
                  <CustomRoute path="/ai-security/overview" exact>
                    <RedirectToModuleTab basePath={AI_SECURITY_TABS.basePath} tabParam="tab" tab="overview" />
                  </CustomRoute>
                  <CustomRoute path="/ai-security/issues" exact>
                    <RedirectToModuleTab basePath={AI_SECURITY_TABS.basePath} tabParam="tab" tab="findings" />
                  </CustomRoute>
                  <CustomRoute path="/ai-security/models" exact>
                    <RedirectToModuleTab basePath={AI_SECURITY_TABS.basePath} tabParam="tab" tab="inventory" />
                  </CustomRoute>
                  <CustomRoute path="/ai-security/ai-workflows" exact>
                    <RedirectToModuleTab basePath={AI_SECURITY_TABS.basePath} tabParam="tab" tab="inventory" />
                  </CustomRoute>
                  <CustomRoute path="/ai-security/mcp-servers" exact>
                    <RedirectToModuleTab basePath={AI_SECURITY_TABS.basePath} tabParam="tab" tab="inventory" />
                  </CustomRoute>
                  <CustomRoute path="/ai-security/scan-history" exact>
                    <RedirectToModuleTab basePath={AI_SECURITY_TABS.basePath} tabParam="tab" tab="overview" />
                  </CustomRoute>
                  <CustomRoute path="/ai-security" exact component={AISecurity} />

                  <CustomRoute path="/rules" exact component={RulesNew} />
                  <CustomRoute path="/rules/table" exact component={RulesTable} />

                  <CustomRoute path="/exceptions" exact component={Exceptions} />
                  {/* <CustomRoute path="/settings" exact component={Settings} /> */}
                  <CustomRoute path="/risk" exact component={Risk} />
                  <CustomRoute path="/risk/risk-table" exact component={RiskTable} />
                  <CustomRoute path="/risk/risk-table/remediation" exact component={Remediation} />
                  <CustomRoute path="/remediation" exact component={Remediation} />

                  <CustomRoute path="/notifications" component={Notifications} />

                  <CustomRoute path="/rbac" exact>
                    <RedirectToModuleTab basePath={WORKSPACE_BASE_PATH} tabParam={WORKSPACE_TAB_PARAM} tab="rbac" />
                  </CustomRoute>
                  <CustomRoute path="/reports" exact>
                    <RedirectToModuleTab basePath={WORKSPACE_BASE_PATH} tabParam={WORKSPACE_TAB_PARAM} tab="reports" />
                  </CustomRoute>
                  <CustomRoute path="/profile" exact component={Profile} />
                  <CustomRoute path="/integrations" exact>
                    <RedirectToModuleTab
                      basePath={WORKSPACE_BASE_PATH}
                      tabParam={WORKSPACE_TAB_PARAM}
                      tab="integrations"
                    />
                  </CustomRoute>
                  <CustomRoute path="/workspace" exact component={WorkSpace} />
                  <CustomRoute path="/progress" exact component={ProgressTracker} />
                  {/* <CustomRoute path="/events" exact>
                    {' '}
                    <Redirect to={`/events/all${search}`} />
                  </Route> */}

                  <CustomRoute path="/events/all" exact>
                    <RedirectToModuleTab
                      basePath={THREAT_TABS.basePath}
                      tabParam={THREAT_TABS.tabParam}
                      tab="events"
                      nestedTab="Drift"
                    />
                  </CustomRoute>
                  <CustomRoute path="/events/suppressed" exact>
                    <RedirectToModuleTab
                      basePath={THREAT_TABS.basePath}
                      tabParam={THREAT_TABS.tabParam}
                      tab="events"
                      nestedTab="ActionHistory"
                    />
                  </CustomRoute>
                  <CustomRoute path="/events/overview" exact>
                    <RedirectToModuleTab
                      basePath={THREAT_TABS.basePath}
                      tabParam={THREAT_TABS.tabParam}
                      tab="events"
                      nestedTab="Overview"
                    />
                  </CustomRoute>
                  <CustomRoute path="/cdr" exact>
                    <RedirectCdrToThreat />
                  </CustomRoute>
                  <CustomRoute path="/threat" exact component={Threat} />
                  <CustomRoute path="/environments/dashboard/events" exact component={EventsTable} />

                  <CustomRoute path="/kubernetes-live/overview" exact component={KubernetesOverview} />
                  <CustomRoute path="/kubernetes-live/inventory/table" exact component={KubernetesInventoryTable} />
                  <CustomRoute path="/kubernetes-live/inventory" exact component={KubernetesInventory} />
                  <CustomRoute path="/kubernetes-live/identity" exact component={KubernetesIdentity} />
                  <CustomRoute path="/kubernetes-live/compliance" exact component={KubernetesCompliance} />
                  <CustomRoute path="/kubernetes-live/seccomp" exact component={KubernetesSeccomp} />
                  <CustomRoute path="/kubernetes-live/network" exact component={KubernetesNetwork} />
                  <CustomRoute path="/kubernetes-live/runtime" exact component={KubernetesRuntime} />
                  <CustomRoute path="/kubernetes-live/vulnerabilities" exact component={K8Vulnerabilities} />
                  <CustomRoute path="/kubernetes-live/risk-acceptance" exact component={KubernetesRiskAcceptance} />
                  <CustomRoute path="/kubernetes-live/events" exact component={EventsTable} />
                  <CustomRoute path="/kubernetes-live/suppressed" exact component={K8ActionHistory} />
                  <CustomRoute path="/kubernetes-live/malware" exact component={K8Malware} />
                  <CustomRoute path="/kubernetes-live/enforcement" exact component={KubernetesEnforcement} />
                  <CustomRoute path="/kubernetes-live/image-scout" exact component={KubernetesImageScout} />
                  <CustomRoute path="/kubernetes-live/scan-history" exact component={KubernetesScanHistory} />

                  <CustomRoute path="/api-security/overview" exact>
                    <RedirectToModuleTab basePath={API_SECURITY_TABS.basePath} tabParam="tab" tab="overview" />
                  </CustomRoute>
                  <CustomRoute path="/api-security/inventory" exact>
                    <RedirectToModuleTab basePath={API_SECURITY_TABS.basePath} tabParam="tab" tab="inventory" />
                  </CustomRoute>
                  <CustomRoute path="/api-security/findings" exact>
                    <RedirectToModuleTab basePath={API_SECURITY_TABS.basePath} tabParam="tab" tab="findings" />
                  </CustomRoute>
                  {/* Retired tabs, merged into Findings; kept so old links still land there. */}
                  <CustomRoute path="/api-security/risk" exact>
                    <RedirectToModuleTab basePath={API_SECURITY_TABS.basePath} tabParam="tab" tab="findings" />
                  </CustomRoute>
                  <CustomRoute path="/api-security/runtime-findings" exact>
                    <RedirectToModuleTab basePath={API_SECURITY_TABS.basePath} tabParam="tab" tab="findings" />
                  </CustomRoute>
                  <CustomRoute path="/api-security/shadow-apis" exact>
                    <RedirectToModuleTab basePath={API_SECURITY_TABS.basePath} tabParam="tab" tab="shadow-apis" />
                  </CustomRoute>
                  <CustomRoute path="/api-security" exact component={APISecurityOverview} />

                  <CustomRoute path="/database-security/overview" exact>
                    <RedirectToModuleTab basePath={DATABASE_SECURITY_TABS.basePath} tabParam="tab" tab="overview" />
                  </CustomRoute>
                  <CustomRoute path="/database-security/exception" exact>
                    <RedirectToModuleTab basePath={DATABASE_SECURITY_TABS.basePath} tabParam="tab" tab="exceptions" />
                  </CustomRoute>
                  <CustomRoute path="/database-security" exact component={DatabaseSecurityModule} />

                  <CustomRoute path="/finops/overview" exact>
                    <RedirectToModuleTab basePath={FINOPS_BASE_PATH} tabParam={FINOPS_TAB_PARAM} tab="overview" />
                  </CustomRoute>
                  <CustomRoute path="/finops/ai-cost-optimization" exact>
                    <RedirectToModuleTab
                      basePath={FINOPS_BASE_PATH}
                      tabParam={FINOPS_TAB_PARAM}
                      tab="ai-cost-optimization"
                    />
                  </CustomRoute>
                  <CustomRoute path="/finops/cloud-cost-management-stages" exact>
                    <RedirectToModuleTab
                      basePath={FINOPS_BASE_PATH}
                      tabParam={FINOPS_TAB_PARAM}
                      tab="cloud-cost-management-stages"
                    />
                  </CustomRoute>
                  <CustomRoute path="/finops" exact component={FinOps} />

                  <CustomRoute path="/inventory/resources-table/scan-report" exact component={ScanReport2} />
                  {/* The resources table is the Resources tab now. Kept so bookmarks, emailed report
                      links and notification links resolve instead of 404ing. */}
                  <CustomRoute path="/inventory/resources-table" exact component={RedirectToResourcesTab} />
                  {/* Overview / Resources / Key Insights are tabs of this one route, selected by `?tab=`. */}
                  <CustomRoute path="/inventory" exact component={Inventory} />

                  <CustomRoute path="/select_compliance" exact component={CompliancePack} />
                  <CustomRoute path="/dashboard" exact component={DashboardNew} />
                  <CustomRoute path="/data-security/overview-old" exact component={MalwareOverview} />

                  <CustomRoute path="/data-security" exact component={MalwareOverviewNew} />
                  <CustomRoute path="/data-security/overview/new" exact component={MalwareOverviewStatic} />
                  {/* <CustomRoute path="/data-security/findings" exact component={Findings} /> */}
                  <CustomRoute path="/data-security/insights" exact component={BucketProtections} />

                  {/* Explorer is the `?tab=explorer` tab of /inventory now. Both paths are kept so
                      bookmarks and older links resolve instead of 404ing. */}
                  <CustomRoute path="/explorer" exact component={RedirectToExplorerTab} />
                  <CustomRoute path="/visualization" exact component={RedirectToExplorerTab} />
                  <CustomRoute path="/attack-path-analysis" exact component={AttackPathAnalysis} />
                  <CustomRoute path="/viper-ai" exact component={ViperAI} />
                  <CustomRoute path="/ai-assistant" exact component={AIChat} />
                  <CustomRoute path="/discovery" exact component={Discovery} />
                  <CustomRoute component={Error} />
                </Switch>
              </ErrorBoundary>
            ) : (
              <RouteLoader />
            )}
          </Suspense>
        </MainLayout>
      )}
    </>
  );
}

export default AppRoutes;
